/**
 * All Bytes / Bookmarks — ROUTE_INVENTORY §2.12 #24.
 *
 * Extracted from `QuickBytesFilterPanel`. Hand-rolled rather than
 * `TabbedInterface` from `@ai-tutor/ui`, and the reason is worth keeping: that
 * primitive owns its own active state and renders panels, where this switches a
 * **URL parameter** and has no panels at all — the grid below reads `bookmarked`
 * from the query string. Wiring `TabbedInterface` to a controlled URL value
 * meant fighting it on every render, so the visual treatment is mirrored
 * instead of the component reused.
 *
 * ## Why a tab strip and not a filter toggle
 *
 * Bookmarks ARE a filter mechanically — `?bookmarked=1`, same as any other.
 * Framing them as a view is a deliberate lie about the mechanism, because the
 * user's model is "where am I" rather than "what have I narrowed". It is also
 * why `ActiveFilterChips` deliberately excludes `bookmarked` (§2.12 #26): the
 * tab already says where you are, and a chip saying it again would let the
 * filter bar render with nothing else in it.
 */

import { Bookmark } from 'lucide-react';
import { cn } from '@/lib/utils';

interface HighlightsViewTabsProps {
  /** True when the Bookmarks view is selected. */
  bookmarked: boolean;
  onChange: (bookmarked: boolean) => void;
}

export function HighlightsViewTabs({
  bookmarked,
  onChange,
}: HighlightsViewTabsProps) {
  return (
    <div className='border-b border-border px-3 py-2'>
      <div
        role='tablist'
        aria-label='Highlights view'
        className='flex gap-0.5 rounded-md bg-muted/30 p-0.5'
      >
        <TabButton
          label='All Bytes'
          isActive={!bookmarked}
          onClick={() => onChange(false)}
        />
        <TabButton
          label='Bookmarks'
          icon={
            <Bookmark
              className={cn('h-4 w-4', bookmarked && 'fill-current')}
              aria-hidden
            />
          }
          isActive={bookmarked}
          onClick={() => onChange(true)}
        />
      </div>
    </div>
  );
}

interface TabButtonProps {
  label: string;
  icon?: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
}

function TabButton({ label, icon, isActive, onClick }: TabButtonProps) {
  return (
    <button
      type='button'
      role='tab'
      aria-selected={isActive}
      onClick={onClick}
      className={cn(
        // Style mirrors TabbedInterface (variant='default') from
        // @ai-tutor/ui - equal-width, rounded-md pill, gray active
        // fill with subtle shadow, soft hover for inactive tabs.
        'flex flex-1 items-center justify-center gap-1 rounded-sm px-2 py-1 text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 dark:focus-visible:ring-primary/40 max-md:min-h-11',
        isActive
          ? 'bg-secondary text-foreground shadow-sm'
          : 'text-muted-foreground hover:bg-accent hover:text-foreground'
      )}
    >
      {icon}
      {label}
    </button>
  );
}
